import { Injectable } from '@angular/core';
import { v4 } from 'uuid';
import { BehaviorSubject, map, Observable, Subject, tap } from 'rxjs';
import {
  EventSetupWizardStep
} from '@deltager/organizers/admin/modules/events/create-event/model/event-setup-wizard-step.enum';
import { FormControl, FormGroup } from '@angular/forms';
import { getControlsWithRequiredValidator } from '@deltager/organizers/admin/shared/helper-functions';

@Injectable()
export class EventWizardValidationStateStore {
  private validationStateMap: Map<number, Map<string, ValidationState>>;
  private _validationRequiredStateMap$ = new BehaviorSubject<Map<EventSetupWizardStep, ValidationRequiredInfo>>(
    new Map<EventSetupWizardStep, ValidationRequiredInfo>());
  private submitSubject: Subject<number>;
  private validationStateSubject: BehaviorSubject<ValidationState>;

  public validationRequiredStateMap$ = this._validationRequiredStateMap$.asObservable();

  get eventStepSubmit(): Observable<number> {
    return this.submitSubject.asObservable();
  }

  get stepValidationState(): Observable<ValidationState> {
    return this.validationStateSubject.asObservable();
  }

  constructor() {
    this.validationStateMap = new Map<number, Map<string, ValidationState>>([]);
    this.submitSubject = new Subject<number>();
    this.validationStateSubject = new BehaviorSubject<ValidationState>({valid: false, status: 'INVALID', context: 'EDIT'});
  }

  registerPartialStep(stepInd: number): string {
    const key = v4();


    if (!this.validationStateMap.has(stepInd)){
      this.validationStateMap.set(stepInd, new Map<string, ValidationState>());
    }
    const partialStepMap = this.validationStateMap.get(stepInd);
    partialStepMap.set(key, {valid: false, status: 'INVALID', context: 'EDIT'});

    return key;
  }

  registerRequiredControl(step: EventSetupWizardStep, key: string, totalControlsLength: number): void {
    const validationRequiredStateMap = this._validationRequiredStateMap$.value;
    if (!validationRequiredStateMap.has(step)) {
      validationRequiredStateMap.set(step, {} as ValidationRequiredInfo)
    }
    const stepSate = validationRequiredStateMap.get(step);
    stepSate.partialStepsMap ??= new Map<string, {validControlsLength: number; totalControlsLength: number}>();

    if (stepSate.partialStepsMap.has(key)) {
      return;
    }
    stepSate.partialStepsMap.set(key, {
      validControlsLength: totalControlsLength,
      totalControlsLength: totalControlsLength
    });

    this._validationRequiredStateMap$.next(validationRequiredStateMap);
  }

  updateStepValidity(step: number, key: string, state: boolean, status: string): void {
    if (!this.validationStateMap.has(step))
      return;

    const partialStepMap = this.validationStateMap.get(step);
    partialStepMap.set(key, {valid: state, status: this.mapToValidationStatus(status), context: 'EDIT'});

    this.validationStateSubject.next({valid: this.isStepValid(step), status: this.getStepStatus(step), context: 'EDIT'});
  }

  getStepStatus(step: number): ValidationStatus {
    if(this.validationStateMap.size === 0) {
      return 'INVALID';
    }

    if (Array.from(this.validationStateMap.get(step).values()).some(v => v.status === 'PENDING')) {
      return 'PENDING';
    }
    if (this.isStepValid(step)) {
      return 'VALID';
    }

    return 'INVALID';
  }

  updateRequiredStepState(step: EventSetupWizardStep, key: string, requiredStateDetail: RequiredStateDetail): void {
    const validationRequiredStateMap = this._validationRequiredStateMap$.value;
    const stepInfo = validationRequiredStateMap.get(step);
    stepInfo.partialStepsMap.set(key, requiredStateDetail);
    this._validationRequiredStateMap$.next(validationRequiredStateMap);
  }

  listenRequiredControlsStateChange$(formGroup: FormGroup, step: EventSetupWizardStep, key: string): Observable<unknown> {
    return formGroup.statusChanges.pipe(
      map(_ => getControlsWithRequiredValidator(formGroup)),
      tap((controls: FormControl[]) =>
        this.updateRequiredStepState(step, key, {
          validControlsLength: controls.filter(control => control.status === 'VALID').length,
          totalControlsLength: controls.length
        }))
    );
  }

  isStepValid(stepIdx: number): boolean {
    return !this.validationStateMap.has(stepIdx)
      || Array.from(this.validationStateMap.get(stepIdx).values())
        .every(state => state.status === 'VALID');
  }


  submitStep(step: number): void {
    this.submitSubject.next(step);

    if (!this.isStepValid(step)) {
      this.validationStateSubject.next({valid: this.isStepValid(step), status: this.getStepStatus(step), context: 'SUBMIT'});
    }
  }

  clearPartialSteps(): void {
    this.validationStateMap.clear();
  }

  clearRequiredValidationInfo(step: EventSetupWizardStep): void {
    const validationRequiredStateMap = this._validationRequiredStateMap$.value;
    validationRequiredStateMap.delete(step);
    this._validationRequiredStateMap$.next(validationRequiredStateMap);
  }

  dispose(): void {
    this.validationStateSubject.complete();
    this.submitSubject.complete();
  }

  private mapToValidationStatus(value: string): ValidationStatus {
    switch (value) {
      case 'INVALID': {
        return 'INVALID';
      }
      case 'PENDING': {
        return 'PENDING';
      }
      case 'VALID': {
        return 'VALID';
      }
      default: {
        return 'INVALID';
      }
    }
  }
}

type ValidationStatus =  'INVALID' | 'VALID' | 'PENDING';

type ValidationContext = 'SUBMIT' | 'EDIT';

type ValidationState = { valid: boolean, status: ValidationStatus, context: ValidationContext }

type RequiredStateDetail = {validControlsLength: number, totalControlsLength: number};
type ValidationRequiredInfo = {
  partialStepsMap: Map<string, RequiredStateDetail>
};

export const invalidSubmitted = (validationState: ValidationState): boolean => validationState.context === 'SUBMIT' && !validationState.valid;
export const edited = (validationState: ValidationState): boolean => validationState.context === 'EDIT';
